import { useQuery } from '@tanstack/react-query';

import type { GetMarkAssembledResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get assembly data from a purchased vehicle
 */
export const getMarkAssembled = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetMarkAssembledResponse> => {
	const response = await apiClient.get<GetMarkAssembledResponse>(
		ordersEndpoints.markAssembled(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting assembly data from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetMarkAssembled = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx.markAssembled.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get assembly data without an order ID.');
			}
			return getMarkAssembled(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
